import { Listbox } from '@headlessui/react';
import clsx from 'clsx';
import { FC, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import Chip from '../chip/chip';
import Icon from '../icon/icon';
import { Text } from '../text/text';

/*
    To conect with Formik headless ui
    component has to use useField
    or setFeildValue
*/

type Option = {
    value: string;
    label: string;
    disable?: boolean;
};

type SelectColors = 'primary' | 'variant';

export type Position = 'top' | 'bottom';

interface SelectUncontrolledI<T> {
    'data-test-id'?: string;
    'data-testid'?: string;
    name: string;
    label?: string;
    labelClasses?: string;
    classes?: string;
    optionsClasses?: string;
    iconClasses?: string;
    defaultValue: T extends Array<string> ? string[] : string;
    options: Option[];
    onChange?(option: T): void;
    onBlur?(): void;
    color?: SelectColors;
    required?: boolean;
    maxVisibleOptions?: number;
    hasVoidOption?: boolean; // agrega una opcion al inicio que retorna valor null
    voidOptionLabel?: string;
    position?: Position;
    positionPointer?: { top?: string; bottom?: string; left?: string; right?: string };
    disabled?: boolean;
    placeholder?: string;
    visualLabel?: string;
    buttonClasses?: string;
    buttonIconSize?: number;
    allowEmptyOptionValue?: boolean;
    /** Controls how selected values are displayed when defaultValue is an array.
     *  'chips' (default) renders removable chip tags.
     *  'comma' renders a single comma-separated text string. */
    displayMode?: 'chips' | 'comma';
    /** Prevents closing dropdown when multiselect is empty. */
    keepOpenWhileEmpty?: boolean;
}

const maxVisibleOptionDefault = 10;
// this height is according to tailwind class h-8, if you change the class please update this option
const optionHeight = 32;

export const SelectUncontrolled: FC<SelectUncontrolledI<string | string[]>> = (props) => {
    const selectColors: Record<SelectColors, string> = {
        primary: 'ui-open:border-primary-500',
        variant: 'ui-open:border-variant-800',
    };

    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const isMultiselect = Array.isArray(props.defaultValue);
    const isEmpty = isMultiselect && props.defaultValue.length === 0;
    const shouldKeepOpen = props.keepOpenWhileEmpty && isMultiselect && isEmpty;

    const getPositionClasses = (): string => {
        if (props.positionPointer) {
            return '';
        }

        if (props.position === 'top') {
            return props.label ? 'bottom-[62px]' : 'bottom-[38px]';
        }

        return props.label ? 'top-[62px]' : 'top-[38px]';
    };
    const getOptionFromValue = (options: Option[], value: string | string[]): Option | undefined => {
        if (props.allowEmptyOptionValue || value !== '') {
            const foundOption = options.find((item) => item.value === value);
            if (foundOption) return foundOption;
        }

        return value === '' ? { value: '', label: props.voidOptionLabel as string } : undefined;
    };
    const mainContainerClasses = twMerge('relative', props.classes);

    const buttonClasses = twMerge(
        'flex justify-between w-full min-h-[38px] p-2 group rounded',
        'text-sm font-roboto font-normal text-surface-high',
        'border border-gray-100 disabled:border-gray-50 disabled:bg-gray-50 disabled:text-stone-500',
        'ui-open:rounded-b-none',
        selectColors[props.color || 'primary'],
        props.buttonClasses,
    );

    const itemClasses = twMerge(
        ' peer bg-white hover:bg-gray-50 text-sm font-roboto font-normal text-surface-high leading-8 cursor-pointer z-20',
        props.optionsClasses,
    );

    const handleSelection = (option: string | string[]) => {
        props.onChange && props.onChange(option);
    };

    const handleBlur = () => {
        // When multiselect is empty and keepOpenWhileEmpty is true, reopen by focusing button
        if (shouldKeepOpen) {
            setTimeout(() => {
                buttonRef.current?.focus();
            }, 0);
        }
        props.onBlur && props.onBlur();
    };

    const handleDeleteSelection = (option: string) => {
        if (Array.isArray(props.defaultValue)) {
            handleSelection(props.defaultValue.filter((item) => item !== option));
        }
    };

    const getOptionClasses = (isSelected: boolean, isDisabled?: boolean): string =>
        clsx(
            'flex min-h-8 py-1 items-center font-roboto text-sm px-2',
            isDisabled ? 'text-gray-300 cursor-not-allowed' : 'text-surface-high hover:bg-gray-50 cursor-pointer',
            isSelected && !isDisabled ? 'bg-primary-50' : 'bg-white',
        );

    const visualLabel = props.visualLabel || getOptionFromValue(props.options, props.defaultValue)?.label;

    return (
        <Listbox
            data-testid={`${props.name}-select-uncontrolled-listbox`}
            name={props.name}
            value={props.defaultValue}
            onChange={handleSelection}
            multiple={Array.isArray(props.defaultValue)}
            disabled={props.disabled}
        >
            {() => {
                return (
                    <div className={mainContainerClasses}>
                        {props.label && (
                            <Listbox.Label
                                className={twMerge('font-roboto font-medium text-black text-sm', props.labelClasses)}
                            >
                                {props.label}
                            </Listbox.Label>
                        )}
                        {props.required && <span className="text-red-600">&nbsp; * </span>}
                        <Listbox.Button
                            ref={buttonRef}
                            className={buttonClasses}
                            onBlur={handleBlur}
                            placeholder={props.placeholder}
                            data-testid={props['data-testid'] || `${props.name}-select-button`}
                            data-test-id={props['data-test-id'] || `${props.name}-select-button`}
                        >
                            {({ open }) => (
                                <>
                                    {Array.isArray(props.defaultValue) ? (
                                        props.displayMode === 'comma' ? (
                                            props.defaultValue.length > 0 ? (
                                                <Text
                                                    className={
                                                        'whitespace-nowrap mr-2 flex items-center overflow-hidden'
                                                    }
                                                >
                                                    <span className="truncate">
                                                        {props.defaultValue
                                                            .map(
                                                                (item) =>
                                                                    getOptionFromValue(props.options, item)?.label ??
                                                                    item,
                                                            )
                                                            .join(', ')}
                                                    </span>
                                                </Text>
                                            ) : (
                                                <p className="text-gray-300">{props.placeholder}</p>
                                            )
                                        ) : (
                                            <div className="w-11/12 flex flex-wrap">
                                                {props.defaultValue.map((item) => (
                                                    <Chip
                                                        className="m-0"
                                                        text={getOptionFromValue(props.options, item)?.label ?? ''}
                                                        size="xxs"
                                                        onClose={(evt) => {
                                                            evt?.preventDefault();
                                                            handleDeleteSelection(item);
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        )
                                    ) : visualLabel ? (
                                        <Text className={'whitespace-nowrap mr-2 flex items-center'}>
                                            {visualLabel}
                                        </Text>
                                    ) : (
                                        <p className="text-gray-300">{props.placeholder}</p>
                                    )}
                                    {!props.disabled && (
                                        <Icon
                                            size={props.buttonIconSize || 18}
                                            color="black"
                                            name="ri-arrow-down-s-line"
                                            className={twMerge(
                                                ' mr-2 transition-all',
                                                open && 'rotate-180',
                                                props.iconClasses,
                                            )}
                                        />
                                    )}
                                </>
                            )}
                        </Listbox.Button>
                        <Listbox.Options
                            data-testid={`${props.name}-select-uncontrolled-options`}
                            className={twMerge(
                                'w-fit py-2 bg-white rounded-b shadow-[0px_3px_10px_0px_rgba(20,115,247,0.12)] overflow-auto absolute left-0 right-0 z-[21] border border-gray-50',
                                !props.positionPointer &&
                                    (props.label
                                        ? props.position === 'top'
                                            ? 'bottom-[62px]'
                                            : 'top-[62px]'
                                        : props.position === 'top'
                                        ? 'bottom-[38px]'
                                        : 'top-[38px]'),
                                getPositionClasses(),
                            )}
                            style={{
                                maxHeight: '300px',
                                ...(props.positionPointer ? props.positionPointer : {}),
                            }}
                        >
                            {props.hasVoidOption && (
                                <Listbox.Option
                                    data-testid={`${props.name}-select-uncontrolled-option-void`}
                                    className={itemClasses}
                                    key="void"
                                    value=""
                                    disabled={!props.hasVoidOption}
                                >
                                    {({ selected }) => {
                                        const optionClasses = getOptionClasses(selected);
                                        return <div className={optionClasses}>{props.voidOptionLabel}</div>;
                                    }}
                                </Listbox.Option>
                            )}
                            {props.options.map((option: Option, index) => (
                                <Listbox.Option
                                    data-testid={`${props.name}-select-uncontrolled-option-${index}`}
                                    className={itemClasses}
                                    key={option.value}
                                    value={option.value}
                                    disabled={option.disable}
                                >
                                    {({ selected }) => {
                                        const optionClasses = getOptionClasses(selected, option.disable);
                                        return Array.isArray(props.defaultValue) ? (
                                            <div className={optionClasses}>
                                                <Icon
                                                    name={selected ? 'ri-checkbox-fill' : 'ri-checkbox-blank-line'}
                                                    color={
                                                        option.disable
                                                            ? 'gray-300'
                                                            : selected
                                                            ? props.color === 'primary'
                                                                ? 'primary'
                                                                : 'variant-800'
                                                            : 'default'
                                                    }
                                                    size={24}
                                                />
                                                <p className="ml-1">{option.label}</p>
                                            </div>
                                        ) : (
                                            <div className={optionClasses}>{option.label}</div>
                                        );
                                    }}
                                </Listbox.Option>
                            ))}
                        </Listbox.Options>
                    </div>
                );
            }}
        </Listbox>
    );
};

SelectUncontrolled.defaultProps = {
    color: 'primary',
};

export default SelectUncontrolled;
